/**
 * DXF HEADER persistence for the AutoCAD running object-snap system variable
 * ($OSMODE) — OSNAP-HEADER-ROUNDTRIP.
 *
 * AutoCAD writes the running-osnap bitcode into the HEADER section as an
 * alternating `9 $NAME` + `<code> <value>` pair:
 *
 *   $OSMODE — int16 (group 70) — running object-snap modes bitcode. Each set
 *             bit enables one snap (1 = Endpoint, 2 = Midpoint, 4 = Center,
 *             8 = Node, 16 = Quadrant, 32 = Intersection, 64 = Insertion,
 *             128 = Perpendicular, 256 = Tangent, 512 = Nearest,
 *             1024 = Geometric Center, 2048 = Apparent Intersection,
 *             4096 = Extension,
 *             8192 = Parallel; 16384 = osnap suppressed). AutoCAD's
 *             out-of-the-box default is 4133.
 *
 * SCOPE NOTE — $APERTURE and $AUTOSNAP are intentionally NOT round-tripped
 * here: neither is a DXF HEADER variable. Verified absent from the ezdxf
 * `HEADER_VAR_MAP` (`ezdxf/sections/headervars.py`), the Rust `dxf` crate's
 * `Header` struct, and the Autodesk DXF R14 HEADER-section group-code table —
 * all three list `$OSMODE` (code 70) but neither `$APERTURE` nor `$AUTOSNAP`.
 * They live only as runtime CAD-session sysvars (registry/AutoSnap settings,
 * surfaced in the web app's `sysvarSchema.ts`), not in the drawing file. Wiring
 * a fabricated `$OSMODE`-style header var for them would invent a code AutoCAD
 * never writes — forbidden by the round-trip "never infer" rule.
 *
 * Read/write/patch mechanics are shared with the document-level and
 * linetype-scale vars via `headerVarCodec.ts` (the generic numeric HEADER-var
 * codec); this module owns only the typed object and the var-table. The store
 * arm lifts the decoded value into the typed `DrawingDocument.osmode` field.
 *
 * AutoCAD reference: drawing system-variable reference 2018+; DXF HEADER section
 * group-code table (R14+). ezdxf `headervars.py` confirms `$OSMODE` is group
 * code 70 (int16). Default 4133 = Autodesk's documented OSMODE default
 * (Endpoint+Center+Intersection+Extension = 1+4+32+4096). NOTE: ezdxf's
 * new-document default is 20583, which sets the 16384 "object-snap suppressed"
 * bit — that is ezdxf's create-doc state, NOT AutoCAD's interactive default, so
 * an absent-`$OSMODE` file must materialise 4133 to match AutoCAD (@codex #333).
 */

import {
  patchNumericVarsIntoHeaderGroups,
  readNumericVarsFromHeader,
  writeNumericVarsToHeaderLines,
  type HeaderVarSpec,
} from "./headerVarCodec";
import type { DxfGroup } from "./types";

// ── Public types ─────────────────────────────────────────────────────────

/**
 * Decoded value for the running object-snap HEADER variable. The field is
 * optional so a legacy DXF without `$OSMODE` yields an empty object — the
 * bridge layer applies the schema default (`osmode: 4133`).
 */
export type HeaderSnapVars = {
  /** $OSMODE — group 70 — running object-snap modes bitcode. */
  osmode?: number;
};

/**
 * Var table: field key on {@link HeaderSnapVars} for `$OSMODE`, plus the DXF
 * group code that carries its value. `$OSMODE` is an int16 flag (group 70).
 * Drives the generic reader / writer / patch so the encoding stays symmetric.
 *
 * `defaultValue: 4133` is AutoCAD's out-of-the-box default. An ABSENT var is
 * only emitted when its value differs from this default (so a default source is
 * never invented into a header that lacked `$OSMODE`); a var ALREADY PRESENT in
 * the header is always overwritten in-place, so an edit back to 4133 persists.
 * Same contract as `headerDocumentVars` (whose default is 0). See
 * `patchNumericVarsIntoHeaderGroups`.
 */
const VAR_FIELDS: ReadonlyArray<HeaderVarSpec<HeaderSnapVars>> = [
  { name: "$OSMODE", field: "osmode", code: 70, int: true, defaultValue: 4133 },
];

// ── Read ─────────────────────────────────────────────────────────────────

/**
 * Scan a HEADER section's flat group list and extract the running-osnap sysvar.
 * Delegates to the shared numeric-var codec; unknown variables and a name match
 * with an unexpected value code are ignored.
 */
export function readSnapVarsFromHeader(
  headerGroups: ReadonlyArray<DxfGroup>,
): HeaderSnapVars {
  return readNumericVarsFromHeader(headerGroups, VAR_FIELDS);
}

// ── Write ────────────────────────────────────────────────────────────────

/**
 * Produce the DxfGroup pairs for the running-osnap HEADER variable. Symmetric
 * with `readSnapVarsFromHeader` — round-trip safe. Only emitted when present and
 * non-default (so a default-valued absent source is never invented).
 */
export function writeSnapVarsToHeaderLines(vars: HeaderSnapVars): DxfGroup[] {
  return writeNumericVarsToHeaderLines(vars, VAR_FIELDS);
}

// ── Patch ────────────────────────────────────────────────────────────────

/**
 * Splice the running-osnap variable entry into an existing HEADER section group
 * array: replace the value group following the matching `9 $OSMODE` entry, then
 * append `$OSMODE` when absent (only if non-default). Every other HEADER
 * variable is preserved verbatim, keeping the file as close to the original
 * AutoCAD output as possible.
 */
export function patchSnapVarsIntoHeaderGroups(
  existingGroups: ReadonlyArray<DxfGroup>,
  vars: HeaderSnapVars,
): DxfGroup[] {
  return patchNumericVarsIntoHeaderGroups(existingGroups, vars, VAR_FIELDS);
}
